import { Box, MenuItem, Select, Typography, CircularProgress } from "@mui/material";
import React, { FC, useState, useEffect } from "react";
import { Colors } from "../../../configs";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { updateCartSessionItem } from "../../../redux/actions/cartAction";
import { CartSliceAction } from "../../../redux/slice/cartSlice";
import { getAllRedoReasonsAction } from "../../../redux/actions/redoReasonsAction";
import { cloneDeep } from "lodash";
import { CustomButton } from "../../atoms";
import CustomModal from "../../common/custom-modal/custom-modal";

const RedoModal: FC = () => {
    const dispatch = useAppDispatch();
    const { isRedoModalOpen, selectedCartItem } = useAppSelector((state) => state.cart);
    const { redoReasons, redoReasonsLoading } = useAppSelector((state) => state.redoReason);

    const [ticketNo, setTicketNo] = useState("");
    const [reason, setReason] = useState("");
    const [comment, setComment] = useState("");
    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => {
        if (isRedoModalOpen && (!redoReasons || redoReasons.length === 0)) {
            dispatch(getAllRedoReasonsAction());
        }
    }, [isRedoModalOpen, dispatch, redoReasons]);

    const handleClose = () => {
        dispatch(CartSliceAction.setIsRedoModalOpen(false));
        // Reset form on close
        setTicketNo("");
        setReason("");
        setComment("");
    };

    const handleRedo = () => {
        if (selectedCartItem) {
            const clonedCartItem = cloneDeep(selectedCartItem);
            clonedCartItem.redoDetails = {
                ticketNo,
                reason,
                comment,
            };
            dispatch(updateCartSessionItem({ cartItem: clonedCartItem }));
        }
        handleClose();
    };

    return (
        <CustomModal
            title="Redo"
            openModal={isRedoModalOpen}
            onClose={handleClose}
            width="40rem"
            height="auto" // Let the content dictate the height
            titleSize="2rem"
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "1.5rem",
                    paddingTop: "1rem",
                }}
            >
                {/* Ticket No Input */}
                <Box>
                    <Typography sx={{ color: Colors.TEXT_GRAY_DARK, fontSize: "1.2rem", mb: "0.5rem" }}>
                        Ticket No. (Optional)
                    </Typography>
                    <input
                        placeholder="xxxxx"
                        autoComplete="off"
                        value={ticketNo}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTicketNo(e.target.value)}
                        style={{
                            width: "100%",
                            border: `0.0714rem solid ${Colors.ACCENT_GRAY_DISABLED}`,
                            borderRadius: "0.714rem",
                            padding: "0.8rem",
                            fontSize: "1.14rem",
                            height: "3.5rem",
                            transition: "all 0.2s ease",
                            fontFamily: "inherit",
                            outline: "none",
                        }}
                    />
                </Box>

                {/* Reason Dropdown */}
                <Box>
                    <Typography sx={{ color: Colors.TEXT_GRAY_DARK, fontSize: "1.2rem", mb: "0.5rem" }}>
                        Reason for the Redo
                    </Typography>
                    <Select
                        value={reason}
                        onChange={(e) => setReason(e.target.value as string)}
                        displayEmpty
                        sx={{
                            width: "100%",
                            borderRadius: "0.714rem",
                            height: "3.5rem",
                            fontSize: "1.14rem",
                            "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: Colors.ACCENT_GRAY_DISABLED,
                            },
                        }}
                    >
                        <MenuItem value="" disabled>
                            Select Reason
                        </MenuItem>
                        {redoReasonsLoading ? (
                            <MenuItem value="" disabled>
                                <CircularProgress size={20} /> Loading Reasons...
                            </MenuItem>
                        ) : (
                            redoReasons && redoReasons.map((redoReason) => (
                                <MenuItem key={redoReason.id} value={redoReason.reason}>
                                    {redoReason.reason}
                                </MenuItem>
                            ))
                        )}
                        {!redoReasonsLoading && (!redoReasons || redoReasons.length === 0) && (
                            <MenuItem value="" disabled>
                                No reasons found.
                            </MenuItem>
                        )}
                    </Select>
                </Box>

                {/* Comment Textarea */}
                <Box>
                    <Typography sx={{ color: Colors.TEXT_GRAY_DARK, fontSize: "1.2rem", mb: "0.5rem" }}>
                        Comment
                    </Typography>
                    <textarea
                        name="redo-comment"
                        style={{
                            border: `0.0714rem solid ${isFocused ? Colors.SECONDARY : Colors.ACCENT_GRAY_DISABLED
                                }`,
                            outline: isFocused ? `0.15rem solid ${Colors.SECONDARY}` : "none",
                            borderRadius: "0.714rem",
                            padding: "0.8rem",
                            fontSize: "1.14rem",
                            height: "6rem",
                            width: "100%",
                            resize: "none",
                            transition: "all 0.2s ease",
                            fontFamily: "inherit",
                        }}
                        onChange={(e) => setComment(e.target.value)}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                        value={comment}
                    />
                </Box>

                {/* Redo Button */}
                <CustomButton
                    sx={{
                        height: "4rem",
                        width: "100%",
                        fontSize: "1.428rem",
                        background: Colors.PRIMARY,
                        color: Colors.WHITE,
                        marginTop: "1rem",
                        "&:hover": {
                            background: Colors.PRIMARY,
                        }
                    }}
                    name="submit-redo"
                    onClick={handleRedo}
                >
                    Redo
                </CustomButton>
            </Box>
        </CustomModal>
    );
};

export default RedoModal;
